import { Breadcrumbs, Tabs } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
    IconBrandGithub,
    IconCheck,
    IconCube,
    IconCubePlus,
    IconExclamationCircle,
    IconKey,
    IconLayersLinked,
    IconRefresh,
} from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type { ContainerResponse } from '@xver/apis/v1/kufer/containers/model';
import ActivationGuard from '@xver/components/Atoms/ActivationGuard';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { AccessBadge, Button, ConfirmActionModal } from '../../../../components/Atoms';
import { useProductAccess } from '../../../../hooks/useActiveProject';
import { focusRowMenu } from '../../../../hooks/useMoveResource';
import useBreadcrumbItems from '../../../../hooks/useBreadcrumbItems';
import usePageHeading from '../../../../hooks/usePageHeading';
import ContainerListTable from './components/ContainerListTable';
import ContainerSummaryTiles from './components/ContainerSummaryTiles';
import CredentialsTabPanel from './components/CredentialsTabPanel';
import GitHubTabPanel from './components/GitHubTabPanel';
import { CONTAINER_LIST_QUERY_KEY, useContainerListQuery } from './hooks/useContainerListQuery';

type TabValue = 'containers' | 'credentials' | 'github';
const VALID_TABS: ReadonlySet<TabValue> = new Set(['containers', 'credentials', 'github']);

function parseTab(value: string | null): TabValue {
    if (value && (VALID_TABS as ReadonlySet<string>).has(value)) {
        return value as TabValue;
    }
    return 'containers';
}

export default function ContainerEnginePage() {
    const { t } = useTranslation('containerEngine');
    const headingRef = usePageHeading(t('page.title', 'Container Engine'));
    const queryClient = useQueryClient();
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const activeTab = parseTab(searchParams.get('tab'));

    const handleTabChange = (next: string | null) => {
        if (!next) return;
        const params = new URLSearchParams(searchParams);
        if (next === 'containers') {
            params.delete('tab');
        } else {
            params.set('tab', next);
        }
        setSearchParams(params, { replace: true });
    };

    const breadcrumbItems = useBreadcrumbItems([
        { title: t('breadcrumb.product', 'Products'), href: '#' },
        {
            title: t('breadcrumb.containerEngine', 'Container Engine'),
            href: '/container-engine',
        },
    ]);

    const { data, isPending, refetch } = useContainerListQuery();
    const containers = useMemo(() => data?.containers ?? [], [data]);
    const containerAccess = useProductAccess('container');
    const isListPending = isPending && containerAccess !== 'none';

    const restartMutation = useMutation({
        mutationFn: (id: string) => Endpoints.v1.Kufer.Containers.restartContainer(id),
        onSuccess: async () => {
            notifications.show({
                title: t('toast.restartTitle', 'Container restart triggered'),
                message: t('toast.restartMessage', 'A new revision is being deployed.'),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await queryClient.invalidateQueries({ queryKey: CONTAINER_LIST_QUERY_KEY });
        },
        onError: (err: { message?: string }) => {
            notifications.show({
                title: t('toast.errorTitle', 'Action failed'),
                message: err?.message ?? t('toast.errorMessage', 'Please try again later.'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => Endpoints.v1.Kufer.Containers.deleteContainer(id),
        onSuccess: async () => {
            notifications.show({
                title: t('toast.deleteTitle', 'Container deleted'),
                message: t('toast.deleteMessage', 'All resources will be cleaned up.'),
                icon: <IconCheck size={18} color="#22c55e" />,
            });
            await queryClient.invalidateQueries({ queryKey: CONTAINER_LIST_QUERY_KEY });
        },
        onError: (err: { message?: string }) => {
            notifications.show({
                title: t('toast.errorTitle', 'Action failed'),
                message: err?.message ?? t('toast.errorMessage', 'Please try again later.'),
                icon: <IconExclamationCircle size={18} color="#eb5757" />,
            });
        },
    });

    const handleRestart = (c: ContainerResponse) => restartMutation.mutate(c.id);

    const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
    const [pendingDelete, setPendingDelete] = useState<ContainerResponse | null>(null);

    const handleDelete = (c: ContainerResponse) => {
        setPendingDelete(c);
        openDelete();
    };

    const confirmDelete = () => {
        if (!pendingDelete) return;
        deleteMutation.mutate(pendingDelete.id, {
            onSuccess: () => {
                closeDelete();
                setPendingDelete(null);
                focusRowMenu('container', pendingDelete.id, true);
            },
        });
    };

    return (
        <div className="w-full min-h-full flex flex-col justify-start items-center gap-8">
            <div className="w-full flex flex-col justify-start items-start">
                <Breadcrumbs separatorMargin={'xs'} className="mb-6">
                    {breadcrumbItems}
                </Breadcrumbs>
                <div className="w-full flex flex-col lg:flex-row justify-between gap-2 lg:gap-0">
                    <div className="flex flex-col justify-start items-start gap-1">
                        <div className="flex flex-row flex-wrap items-center gap-2">
                            <h1 ref={headingRef} tabIndex={-1} className="text-3xl font-bold">
                                {t('page.title', 'Container Engine')}
                            </h1>
                            {containerAccess && containerAccess !== 'manage' && (
                                <AccessBadge level={containerAccess} />
                            )}
                        </div>
                        <p className="w-auto max-w-135 text-base text-fg/68">
                            {t(
                                'page.description',
                                'Deploy and manage containerised applications with automatic scaling and load balancing.'
                            )}
                        </p>
                    </div>
                    <div className="flex flex-row flex-wrap justify-start lg:justify-end items-end gap-2">
                        <Button
                            size="sm"
                            variant="secondary"
                            leftIcon={<IconRefresh stroke={1.5} />}
                            onClick={() => {
                                setIsRefreshing(true);
                                void refetch().finally(() => setIsRefreshing(false));
                            }}
                            isLoading={isRefreshing}
                        >
                            {t('buttons.refresh', 'Refresh')}
                        </Button>
                        <Button
                            href="/artifact-registry"
                            size="sm"
                            variant="secondary"
                            leftIcon={<IconLayersLinked stroke={1.5} aria-hidden="true" />}
                        >
                            {t('buttons.artifact', 'Artifact')}
                        </Button>
                        {containerAccess === 'manage' && (
                            <Button
                                href="/container-engine/deployment"
                                size="sm"
                                leftIcon={<IconCubePlus stroke={1.5} aria-hidden="true" />}
                                className="w-full lg:w-auto"
                            >
                                {t('buttons.deployment', 'Deployment')}
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            <div className="w-full">
                <ActivationGuard>
                    <div className="w-full flex flex-col gap-6">
                        <ContainerSummaryTiles
                            containers={data?.containers}
                            isPending={isListPending}
                        />

                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            color="var(--fg)"
                            keepMounted={false}
                            className="w-full space-y-4"
                        >
                            <Tabs.List aria-label={t('tabs.label')}>
                                <Tabs.Tab value="containers" leftSection={<IconCube size={20} />}>
                                    {t('tabs.containers', 'Containers')}
                                </Tabs.Tab>
                                <Tabs.Tab value="credentials" leftSection={<IconKey size={20} />}>
                                    {t('tabs.credentials', 'Credentials')}
                                </Tabs.Tab>
                                <Tabs.Tab
                                    value="github"
                                    leftSection={<IconBrandGithub size={20} />}
                                >
                                    {t('tabs.github', 'GitHub')}
                                </Tabs.Tab>
                            </Tabs.List>

                            <Tabs.Panel value="containers" className="w-full">
                                <ContainerListTable
                                    containers={containers}
                                    isLoading={isListPending}
                                    canManage={containerAccess === 'manage'}
                                    onRestart={handleRestart}
                                    onDelete={handleDelete}
                                    isMutationPending={
                                        restartMutation.isPending || deleteMutation.isPending
                                    }
                                    emptyMessage={t(
                                        'empty.containers',
                                        'No containers yet. Click Deployment to create your first one.'
                                    )}
                                />
                            </Tabs.Panel>

                            <Tabs.Panel value="credentials" className="w-full">
                                <CredentialsTabPanel />
                            </Tabs.Panel>

                            <Tabs.Panel value="github" className="w-full">
                                <GitHubTabPanel />
                            </Tabs.Panel>
                        </Tabs>
                    </div>
                </ActivationGuard>
            </div>

            <ConfirmActionModal
                opened={deleteOpened}
                onClose={() => {
                    if (deleteMutation.isPending) return;
                    if (pendingDelete) focusRowMenu('container', pendingDelete.id);
                    closeDelete();
                    setPendingDelete(null);
                }}
                title={t('confirm.deleteTitle', 'Delete container?')}
                message={t('confirm.deleteMessage', {
                    name: pendingDelete?.name ?? '',
                    defaultValue:
                        '"{{name}}" will be removed and all resources cleaned up. This cannot be undone.',
                })}
                confirmLabel={t('confirm.deleteConfirmLabel', 'Delete container')}
                confirmVariant="danger"
                isLoading={deleteMutation.isPending}
                onConfirm={confirmDelete}
            />
        </div>
    );
}
